import React from 'react'
import { Form, Input, Modal, Select, Switch } from 'antd'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'

import { Truck, UpdateTruckData } from '@types'
import { useEquipment } from '@hooks'

import { StyledCheckbox, StyledForm, StyledRow } from '../../../../../components/StyledComponents'

type Props = {
  open: boolean
  onClose: () => void
  onEdit: (data: UpdateTruckData) => void
  truckData: Truck
}

export const validationSchema = yup.object().shape({
  licensePlate: yup.string().required('Campo obligatorio'),
  description: yup.string().required('Campo obligatorio'),
  equipmentType: yup
    .object()
    .when('straightTruck', {
      is: true,
      then: yup.object({
        id: yup.number().required('Debe seleccionar una opción'),
      }),
    })
    .optional(),
  straightTruck: yup.boolean(),
  disabled: yup.boolean(),
})

const EditTruckModal: React.FC<Props> = ({ open, onClose, onEdit, truckData }) => {
  const { t } = useTranslation()
  const { equipments } = useEquipment()
  const { reset, handleSubmit, register, setValue, formState, watch, getValues } = useForm<{
    licensePlate: string
    description: string
    straightTruck: boolean
    equipmentType: { id: number | null }
    disabled: boolean
  }>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      licensePlate: truckData.licensePlate,
      description: truckData.description,
      disabled: !!truckData.disableAt,
      straightTruck: !!truckData.straightTruck,
      equipmentType: { id: truckData.equipmentType ? truckData.equipmentType.id : undefined },
    },
  })

  const onSubmit = (updateData: UpdateTruckData) => {
    onEdit({
      ...updateData,
      equipmentType: {
        id:
          !!updateData.straightTruck && updateData.equipmentType?.id
            ? updateData.equipmentType?.id
            : null,
      },
    })
    reset()
  }

  const handleIncludeTrailerChange = (checked: boolean) => {
    setValue('straightTruck', !checked)
  }

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        reset()
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.edit_truck_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.edit_truck_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.edit_truck_modal.button_cancel')}
      onOk={handleSubmit(
        onSubmit as unknown as SubmitHandler<{
          licensePlate: string
          description: string
          straightTruck: boolean
          equipmentType: {
            id: number | null
          }
          disabled: boolean
        }>,
      )}
      destroyOnClose
    >
      <StyledForm>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.licensePlate')}
            name="licensePlate"
          >
            <Input defaultValue={truckData?.licensePlate} disabled />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.description')}
            name="description"
            hasFeedback
            validateStatus={formState.errors.description ? 'error' : ''}
            help={formState.errors.description?.message}
          >
            <Input
              {...register('description')}
              defaultValue={truckData?.description}
              onChange={e => {
                setValue('description', e.target.value)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item label={t('tpl_router.tpl_dashboard.add_truck_modal.tractor_unit')}>
            <StyledCheckbox
              style={{ margin: '0 0 0 0', justifyContent: 'left' }}
              onChange={e => {
                handleIncludeTrailerChange(e.target.checked)
              }}
              defaultChecked={!truckData.straightTruck}
            />
          </Form.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            rules={[
              {
                required: watch('straightTruck'),
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.equipmentType')}
            name="equipmentType.id"
            hasFeedback
            validateStatus={formState.errors.equipmentType ? 'error' : ''}
            help={formState.errors.equipmentType?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={!watch('straightTruck') ? null : t('globals.placeholder_select')}
              options={equipments
                ?.filter(equipment => equipment.straightTruck)
                .map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
              onChange={value => {
                setValue('equipmentType.id', value)
              }}
              defaultValue={truckData.equipmentType?.id ?? undefined}
              disabled={!watch('straightTruck')}
            />
          </Form.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledRow className="carrier-form-row">
            <Form.Item label="Estado" name="disabled">
              <Switch
                onClick={() => {
                  setValue('disabled', !getValues('disabled'))
                }}
                defaultChecked={!truckData.disableAt}
                checkedChildren="HABILITADO"
                unCheckedChildren="DESHABILITADO"
              />
            </Form.Item>
          </StyledRow>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { EditTruckModal }
